import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { HiOutlinePlus, HiOutlineChevronRight, HiOutlineRefresh, HiOutlineCloudDownload, HiOutlineExclamation, HiOutlineTrash } from "react-icons/hi";
import { PiClockCounterClockwise, PiCheckBold } from "react-icons/pi";
import { MdDragIndicator } from "react-icons/md";
import { LuNetwork } from "react-icons/lu";
import { toast } from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";

import API from "../../api";
import { capture } from "../../instrument";
import { useHotkeys } from "react-hotkeys-hook";
import useListShortcuts from "../../hooks/useListShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import { rankBySearch } from "../../utils/search";
import useReorderable from "../../hooks/useReorderable";
import useSelection from "../../hooks/useSelection";
import BulkActionBar from "../../components/selection/BulkActionBar";
import useSavedViews from "../../hooks/useSavedViews";
import { buildTriggerFilterFields, REVIEW_STATUS, readListState, writeListState, hasPendingSync, isShowingDeleted } from "../../utils";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";
import useSearchFilters, { SEARCH_OPERATOR } from "../../hooks/useSearchFilters";
import SearchFilters from "../../components/SearchFilters";
import SaveViewModal from "../../components/SaveViewModal";
import ViewTabs from "../../components/ViewTabs";
import { EFFECT_TYPE_LABELS, EFFECT_TYPE_STYLES } from "../../utils";

const DEFAULT_VIEWS = [
  { _id: "ALL", name: "Toutes", filters: [] },
  { _id: "PENDING", name: "A valider", filters: [{ id: "default-pending", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["PENDING"] }] },
  { _id: "DONE", name: "Validees", filters: [{ id: "default-done", field: "syncStatus", operator: SEARCH_OPERATOR.IS_NOT, values: ["PENDING"] }] },
];

const STORAGE_KEY = "action-list-state";

const Actions = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const savedState = useMemo(() => readListState(STORAGE_KEY), []);
  const [search, setSearch] = useState(savedState?.search || "");
  const [showDeleted, setShowDeleted] = useState(isShowingDeleted(savedState?.filters));

  const listRef = useRef(null);
  const selectedIndexRef = useRef(savedState?.selectedIndex || 0);
  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex || 0);

  const {
    data: actions = [],
    isLoading: isLoadingActions,
    isError: isErrorActions,
  } = useQuery({
    queryKey: ["actions", { showDeleted }],
    queryFn: async () => {
      const response = await API.get(`/action?includeDeleted=${showDeleted}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
  });

  const reorderMutation = useMutation({
    mutationFn: ({ movedId, beforeId, afterId }) => API.post("/action/reorder", { id: movedId, beforeId, afterId }),
    onSuccess: (response) => {
      if (!response.ok) {
        toast.error(response.message);
        queryClient.invalidateQueries({ queryKey: ["actions"] });
        return;
      }
      toast.success("Ordre mis à jour");
      queryClient.invalidateQueries({ queryKey: ["actions"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la mise à jour");
      queryClient.invalidateQueries({ queryKey: ["actions"] });
    },
  });

  const { data: sharingNetwork } = useQuery({
    queryKey: ["network-config"],
    queryFn: async () => {
      const response = await API.get("/network-config");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    staleTime: Infinity,
  });

  const filterFields = useMemo(() => {
    const uniqueEffectTypes = [...new Set(actions.flatMap((a) => a.effects?.map((e) => e.type)).filter(Boolean))].sort();
    const uniqueSyncSources = [...new Set(actions.flatMap((a) => a._syncs?.map((s) => s.source)).filter(Boolean))].sort();
    const triggerFilters = buildTriggerFilterFields(actions, [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT]);

    return [
      {
        key: "effectType",
        label: "Type d'effet",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueEffectTypes,
        resolve: (action) => action.effects?.map((e) => e.type) || [],
      },
      ...triggerFilters,
      {
        key: "syncStatus",
        label: "Status synchronisation",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: ["PENDING", "SYNCED", "DELETED"],
        resolve: (action) => {
          if (!action._syncs?.length) return null;
          if (action._syncs.some((s) => s.reviewStatus === REVIEW_STATUS.PENDING)) return "PENDING";
          if (action._syncs.some((s) => s.deletedFromSource)) return "DELETED";
          return "SYNCED";
        },
      },
      {
        key: "syncSource",
        label: "Source synchronisation",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueSyncSources,
        resolve: (action) => action._syncs?.map((s) => s.source).filter(Boolean) || [],
      },
      {
        key: "deleted",
        label: "Supprimée",
        operators: [SEARCH_OPERATOR.IS],
        values: ["Oui", "Non"],
        resolve: (action) => (action.isDeleted ? "Oui" : "Non"),
      },
      ...(sharingNetwork
        ? [
            {
              key: "network",
              label: "Réseau",
              operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
              values: ["PROPRIETAIRE", "RECU", "AUCUN"],
              valueLabels: { PROPRIETAIRE: "Partagée (propriétaire)", RECU: "Reçue du réseau", AUCUN: "Non partagée" },
              resolve: (action) => (action.sharedToNetwork ? "PROPRIETAIRE" : action.networkSourceId ? "RECU" : "AUCUN"),
            },
          ]
        : []),
    ];
  }, [actions, sharingNetwork]);

  const { filters: activeFilters, setFilters: setActiveFilters, applyFilters } = useSearchFilters(filterFields, { initialFilters: savedState?.filters });

  const onFiltersChange = (nextFilters) => {
    setActiveFilters(nextFilters);
    setShowDeleted(isShowingDeleted(nextFilters));
    resetSelection();
  };

  const { savedViews, activeViewId, saveModalOpen, setSaveModalOpen, handleSelectView, isViewActive, handleConfirmSaveView, handleDeleteView, saveOptions } = useSavedViews({
    collectionName: "action",
    defaultViews: DEFAULT_VIEWS,
    activeFilters,
    setActiveFilters: onFiltersChange,
    initialViewId: savedState?.viewId,
  });

  useEffect(() => {
    writeListState(STORAGE_KEY, { search, filters: activeFilters, viewId: activeViewId });
  }, [search, activeFilters, activeViewId]);

  const filterCounts = useMemo(() => {
    const pendingCount = actions.filter((a) => hasPendingSync(a)).length;
    const reviewedCount = actions.filter((a) => !hasPendingSync(a)).length;
    return { pending: pendingCount, reviewed: reviewedCount, all: actions.length };
  }, [actions]);

  const getViewBadge = (tab) => {
    if (tab._id === "ALL") return <span className="ml-1.5 text-xs text-gray-400">{filterCounts.all}</span>;
    if (tab._id === "PENDING" && filterCounts.pending > 0)
      return <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">{filterCounts.pending}</span>;
    if (tab._id === "DONE") return <span className="ml-1.5 text-xs text-gray-400">{filterCounts.reviewed}</span>;
    return null;
  };

  const filteredActions = useMemo(() => {
    const result = applyFilters(actions);
    return rankBySearch(result, search, (action) => [action.name, ...(action.effects?.flatMap((e) => e.values) ?? [])].filter(Boolean).join(" "));
  }, [actions, search, activeFilters]);

  const orderedIds = useMemo(() => filteredActions.map((a) => a._id), [filteredActions]);
  const selection = useSelection(orderedIds);
  const selectedShareableIds = useMemo(
    () => actions.filter((a) => selection.selected.has(a._id) && !a.isDeleted && !a.sharedToNetwork && !a.networkSourceId).map((a) => a._id),
    [actions, selection.selected],
  );

  const shareMutation = useMutation({
    mutationFn: () => API.post("/network-config/action", { fromIds: selectedShareableIds }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message || "Une erreur est survenue");
      const { processed = 0, skipped = 0, errors = [] } = response.data || {};
      if (processed === 0 && errors.length > 0) return toast.error(errors[0].message);
      let message = `${processed} action(s) partagée(s) au réseau`;
      if (skipped > 0) message += ` — ${skipped} ignorée(s)`;
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ["actions"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      selection.clear();
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const {
    getDragProps,
    isDragging: isItemDragging,
    showDropBefore,
    showDropAfter,
  } = useReorderable(
    filteredActions,
    (newItems, { toIndex }) => {
      const movedId = newItems[toIndex]._id;
      const beforeId = toIndex > 0 ? newItems[toIndex - 1]._id : null;
      const afterId = toIndex < newItems.length - 1 ? newItems[toIndex + 1]._id : null;
      queryClient.setQueryData(["actions", { showDeleted }], newItems);
      reorderMutation.mutate({ movedId, beforeId, afterId });
    },
    { scrollRef: listRef },
  );

  const canDrag = activeFilters?.length === 0 && search.length === 0 && !reorderMutation.isPending;

  const virtualizer = useVirtualizer({
    count: filteredActions.length,
    getScrollElement: () => listRef.current,
    initialOffset: savedState?.scrollTop || 0,
    estimateSize: () => 53,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const resetSelection = () => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    virtualizer.scrollToIndex(0);
    writeListState(STORAGE_KEY, { scrollTop: 0, selectedIndex: 0 });
  };

  const handleNavigateDown = () => {
    const next = selectedIndexRef.current >= filteredActions.length - 1 ? 0 : selectedIndexRef.current + 1;
    selectedIndexRef.current = next;
    setSelectedIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const handleNavigateUp = () => {
    const prev = selectedIndexRef.current <= 0 ? filteredActions.length - 1 : selectedIndexRef.current - 1;
    selectedIndexRef.current = prev;
    setSelectedIndex(prev);
    virtualizer.scrollToIndex(prev);
  };

  const onRowMouseMove = (event, index) => {
    const pointerDidNotMove = event.movementX === 0 && event.movementY === 0;
    if (pointerDidNotMove || selectedIndexRef.current === index) return;
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  useListShortcuts(
    {
      onUp: handleNavigateUp,
      onDown: handleNavigateDown,
      onOpen: ({ newTab } = {}) => {
        const action = filteredActions[selectedIndexRef.current];
        if (!action) return;
        if (newTab) window.open(`/action/${action._id}/edit`, "_blank");
        else navigateToAction(action);
      },
    },
    [filteredActions, virtualizer],
  );

  useHotkeys(SHORTCUTS.CREATE.keys, () => history.push("/action/add"), { preventDefault: true });
  useHotkeys("x", () => selection.select(selectedIndexRef.current), { preventDefault: true, enabled: filteredActions.length > 0 }, [filteredActions.length]);
  useHotkeys(
    "mod+a",
    (e) => {
      e.preventDefault();
      selection.selectAll();
    },
    { preventDefault: true },
    [orderedIds],
  );
  useHotkeys("escape", () => selection.clear(), { enabled: selection.count > 0 }, [selection.count]);

  const navigateToAction = (action) => {
    const ids = filteredActions.map((a) => a._id);
    writeListState(STORAGE_KEY, { scrollTop: listRef.current.scrollTop, selectedIndex: ids.indexOf(action._id) });
    history.push({ pathname: `/action/${action._id}/edit`, state: { filteredIds: ids } });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetSelection();
  };

  if (isLoadingActions) {
    return <PageLoadingSpinner />;
  }

  if (isErrorActions) {
    return (
      <div className="flex min-h-[calc(100vh-60px)] flex-col items-center justify-center gap-3 bg-gray-50">
        <span className="text-sm text-red-500">Erreur lors du chargement des actions</span>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-semibold text-gray-900">Actions</h1>
          <ViewTabs
            defaultViews={DEFAULT_VIEWS}
            savedViews={savedViews}
            isViewActive={isViewActive}
            onSelectView={handleSelectView}
            onDeleteView={handleDeleteView}
            getViewBadge={getViewBadge}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
            onClick={() => window.open("/audit-log/actions", "_blank", "noopener,noreferrer,width=1000,height=800")}
            title="Journal d'audit"
          >
            <PiClockCounterClockwise className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={() => {
              history.push("/action/add");
              notifyShortcut("CREATE");
            }}
          >
            <HiOutlinePlus className="h-4 w-4" />
            <span>Nouvelle action</span>
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <SearchFilters
          fields={filterFields}
          filters={activeFilters}
          onFiltersChange={onFiltersChange}
          search={search}
          onSearchChange={handleSearchChange}
          placeholder="Rechercher une action..."
          saveOptions={saveOptions}
        />

        <div ref={listRef} className="max-h-[calc(100vh-252px)] overflow-y-auto">
          {filteredActions.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const action = filteredActions[virtualRow.index];
                const index = virtualRow.index;
                const dragging = isItemDragging(index);
                const dragProps = canDrag ? getDragProps(index) : {};
                const isSelected = selection.isSelected(action._id);

                return (
                  <div
                    key={action._id}
                    className="relative"
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                  >
                    {showDropBefore(index) && <div className="absolute -top-px left-4 right-4 z-10 h-0.5 rounded-full bg-primary-500" />}
                    <div
                      style={{ height: virtualRow.size }}
                      {...dragProps}
                      data-active={virtualRow.index === selectedIndex || undefined}
                      onMouseMove={(event) => onRowMouseMove(event, virtualRow.index)}
                      data-selected={isSelected || undefined}
                      className={`group flex cursor-pointer items-center border-b border-gray-100 pr-4 transition-all duration-150 hover:bg-gray-50 data-[active=true]:bg-primary-50/50 data-[selected=true]:bg-primary-50 ${
                        dragging ? "scale-[0.98] bg-gray-100 opacity-50" : ""
                      }`}
                      onClick={() => navigateToAction(action)}
                    >
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label="Sélectionner l'action"
                        onClick={(e) => {
                          e.stopPropagation();
                          selection.select(index, { shiftKey: e.shiftKey });
                        }}
                        className="group/check flex shrink-0 items-center self-stretch pl-4 pr-2"
                      >
                        <span
                          className={`flex h-[18px] w-[18px] items-center justify-center rounded-md border transition-all ${
                            isSelected ? "border-primary-500 bg-primary-500 text-white shadow-sm" : "border-gray-300 bg-white text-transparent group-hover/check:border-primary-400"
                          } ${selection.count > 0 || isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                        >
                          <PiCheckBold className="h-2.5 w-2.5" />
                        </span>
                      </button>
                      <MdDragIndicator
                        onClick={(e) => e.stopPropagation()}
                        className={`mr-3 flex-shrink-0 text-lg ${canDrag ? "cursor-grab text-gray-400 active:cursor-grabbing" : "cursor-not-allowed text-gray-200"}`}
                      />
                      <span className="min-w-0 truncate text-sm font-medium text-gray-900">{action.name || "Action sans nom"}</span>

                      <div className="ml-auto flex flex-shrink-0 items-center gap-2 pl-4">
                        {action.effects?.map((effect, i) => (
                          <span
                            key={i}
                            className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${EFFECT_TYPE_STYLES[effect.type] || "bg-gray-50 text-gray-700"}`}
                          >
                            {EFFECT_TYPE_LABELS[effect.type] || effect.type}
                            {effect.values?.length > 0 && <span className="ml-1 opacity-70">({effect.values.length})</span>}
                          </span>
                        ))}
                        {!action.isDeleted && (action.networkSourceId || action.sharedToNetwork) && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                            <LuNetwork className="h-3 w-3" />
                            Réseau
                          </span>
                        )}
                        {action.isDeleted ? (
                          <span className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            <HiOutlineTrash className="h-3 w-3" />
                            Supprimée
                          </span>
                        ) : action._syncs?.some((s) => s.deletedFromSource) ? (
                          <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                            <HiOutlineExclamation className="h-3 w-3" />
                            Supprime
                          </span>
                        ) : hasPendingSync(action) ? (
                          <span className="inline-flex items-center gap-1 rounded-md border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                            <HiOutlineRefresh className="h-3 w-3" />A valider
                          </span>
                        ) : action._syncs?.length > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                            <HiOutlineCloudDownload className="h-3 w-3 text-primary-500" />
                            Sync
                          </span>
                        ) : null}
                        <HiOutlineChevronRight className="h-4 w-4 text-gray-300" />
                      </div>
                    </div>
                    {showDropAfter(index, filteredActions.length) && <div className="absolute -bottom-px left-4 right-4 z-10 h-0.5 rounded-full bg-primary-500" />}
                  </div>
                );
              })}
            </div>
          )}

          {filteredActions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <p className="text-sm">{search || activeFilters.some((f) => f.values.length > 0) ? "Aucun resultat pour les filtres appliques" : "Aucune action trouvée"}</p>
              {!search && activeFilters.length === 0 && <p className="mt-1 text-xs">Créez-en une en cliquant sur le bouton ci-dessus</p>}
            </div>
          )}
        </div>
      </div>

      <BulkActionBar
        count={selection.count}
        total={filteredActions.length}
        noun="action"
        feminine
        loading={shareMutation.isPending}
        onClear={selection.clear}
        actions={[
          {
            key: "share",
            label: "Partager au réseau",
            hidden: !sharingNetwork || selectedShareableIds.length === 0,
            onClick: () => shareMutation.mutate(),
          },
        ]}
      />

      {saveModalOpen && (
        <SaveViewModal
          onClose={() => setSaveModalOpen(false)}
          onConfirm={(name) => {
            setSaveModalOpen(false);
            handleConfirmSaveView(name);
          }}
        />
      )}
    </div>
  );
};

export default Actions;
